import { useCourse } from "api/hooks";
import { useScheduleEvents } from "api/scheduleEvents";
import { PaziaBox, PaziaBreadcrumbs } from "components/atoms";
import { CourseRegistrationSettings, PageMain, CourseScheduleDurationPreview } from "components/molecules";
import { CourseSchedulePreview } from "components/molecules/CourseSchedule";
import { routes } from "consts";
import { COURSE_STATUS_CLASS, COURSE_STATUS_NAMES } from "consts/courseType";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PaziaButton, PaziaLoader } from "components/atoms";
import { ChangeStatusModal } from "./BaseInfo/ChangeStatusModal";
import { ManageCourseBaseInfo } from "./BaseInfo/ManageCourseBaseInfo";
import { CourseExams } from "./CourseExams";
import { CourseFinances } from "./CourseFinances";
import { CourseRatings } from "./CourseRatings";
import { CourseStatus } from "./CourseStatus";

export const CourseManage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const { schedule, isLoading } = useScheduleEvents(courseId!);
  const { data, loading, invalidate } = useCourse(Number(courseId));
  if (loading || !data) return <PaziaLoader position="center" />;

  return (
    <PageMain>
      <div className="flex gap-8">
        <div className="mb-4 flex flex-col">
          <PaziaBreadcrumbs
            segments={[
              { title: "Grupy kursów", path: routes.COURSE_GROUPS },
              { title: data.group.name, path: `${routes.COURSE_GROUPS_EDIT_COURSES(data.group.id.toString())}` },
              { title: [data.data.edition, data.data.variant].filter(Boolean).join(" · ") },
            ]}
          />
        </div>
      </div>
      <div className="grid grow auto-rows-min grid-cols-9 gap-4">
        <PaziaBox
          className="col-span-9 xl:col-span-6"
          onEdit={() => navigate(routes.COURSES_EDIT_NESTED.INFO(courseId))}
          header={data.data.name}
        >
          <div className="h-2"></div>
          <CourseStatus courseId={Number(courseId)} hideStatusLabel />
          <div className="mt-4 border-t border-slate-200 pt-4 dark:border-slate-700">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-300">
                Całościowy status kursu
              </div>
              <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
                <div className="flex items-center gap-2">
                  <div
                    className={`h-2.5 w-2.5 rounded-full ${COURSE_STATUS_CLASS[data.data.status].replace("text-", "bg-")}`}
                  ></div>
                  <span className="font-semibold text-slate-800 dark:text-slate-200">
                    {COURSE_STATUS_NAMES[data.data.status]}
                  </span>
                </div>
                <PaziaButton
                  type="secondary"
                  onClick={() => setStatusModalOpen(true)}
                  className="!h-8 whitespace-nowrap !px-3 !py-1 text-xs font-medium"
                >
                  Zmień
                </PaziaButton>
              </div>
            </div>
          </div>
        </PaziaBox>
        <PaziaBox className="col-span-9 xl:col-span-3" header="Panel akcji">
          <ManageCourseBaseInfo courseId={data.data.id} />
          <div className="mt-4 border-t border-slate-200 pt-4 dark:border-slate-700">
            <CourseRegistrationSettings
              courseId={data.data.id}
              registrationsClosed={data.data.registrationsClosed}
              allowRegistrationAfterStart={data.data.allowRegistrationAfterStart}
              invalidate={invalidate}
            />
          </div>
        </PaziaBox>
        <CourseFinances courseId={Number(courseId)} />
        <PaziaBox className="col-span-9 md:col-span-5" header="Oceny">
          <CourseRatings courseId={Number(courseId)} />
        </PaziaBox>
        <PaziaBox
          className="col-span-9"
          onEdit={() => navigate(routes.COURSES_EDIT_NESTED.SCHEDULE(courseId))}
          header="Harmonogram"
        >
          {!isLoading && schedule && (
            <CourseSchedulePreview
              noDataPlaceholder={<EmptySchedulePlaceholder courseId={Number(courseId)} />}
              schedules={schedule.events}
            />
          )}
        </PaziaBox>
        <PaziaBox
          className="col-span-9 lg:col-span-4"
          onEdit={() => navigate(routes.COURSES_EDIT_NESTED.EXAMS(courseId))}
          header="Egzaminy"
        >
          <CourseExams courseId={Number(courseId)} />
        </PaziaBox>
        <PaziaBox className="col-span-9 lg:col-span-5" header="Podgląd czasu trwania">
          <CourseScheduleDurationPreview courseId={Number(courseId)} />
        </PaziaBox>
      </div>
      <ChangeStatusModal
        isOpen={statusModalOpen}
        course={data.data}
        onCloseRequest={() => {
          setStatusModalOpen(false);
          invalidate();
        }}
      />
    </PageMain>
  );
};
interface EmptySchedulePlaceholderProps {
  courseId: number;
}
const EmptySchedulePlaceholder = ({ courseId }: EmptySchedulePlaceholderProps) => {
  const navigate = useNavigate();
  return (
    <div>
      <div className="flex justify-end">
        <svg className="mr-3 mt-[3px] h-4 w-4 shrink-0 fill-current text-amber-500" viewBox="0 0 16 16">
          <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm0 12c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1zm1-3H7V4h2v5z" />
        </svg>
        Harmonogram nie został jeszcze ustalony.
      </div>
      <div
        className="mt-1 cursor-pointer text-right text-sm font-medium text-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400"
        onClick={() => navigate(`${routes.COURSES_EDIT(courseId)}/${routes.COURSES_EDIT_SCHEDULE}`)}
      >
        Przejdź do edycji -&gt;
      </div>
    </div>
  );
};
